import { useTranslation } from 'react-i18next';

export default function RegionalFocus() {
  const { i18n } = useTranslation();

  const values = [
    {
      icon: 'ri-eye-line',
      titleEn: 'Transparency',
      titleAr: 'الشفافية',
      descEn: 'Clear communication and honest dealings in every transaction, building trust through openness.',
      descAr: 'تواصل واضح وتعاملات صادقة في كل معاملة، بناء الثقة من خلال الانفتاح.'
    },
    {
      icon: 'ri-global-line',
      titleEn: 'Global Network',
      titleAr: 'شبكة عالمية',
      descEn: 'Extensive connections across continents, linking MENA markets with international partners.',
      descAr: 'اتصالات واسعة عبر القارات، تربط أسواق الشرق الأوسط وشمال أفريقيا بالشركاء الدوليين.'
    },
    {
      icon: 'ri-award-line',
      titleEn: 'Experience',
      titleAr: 'الخبرة',
      descEn: 'Years of proven expertise in commercial brokerage and international trade facilitation.',
      descAr: 'سنوات من الخبرة المثبتة في الوساطة التجارية وتسهيل التجارة الدولية.'
    }
  ];

  return (
    <section id="regional" className="py-24 bg-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, #1A5F7A 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-2 mb-4">
            <i className="ri-map-2-line text-2xl text-[#D4AF37]"></i>
            <span className="text-sm font-medium text-[#1A5F7A] uppercase tracking-wider">
              {i18n.language === 'ar' ? 'نطاقنا العالمي' : 'Our Global Reach'}
            </span>
          </div>
          <h2 className="text-5xl font-bold mb-6 leading-tight">
            <div className="text-[#0A2647]">
              {i18n.language === 'ar' ? 'خبرة في منطقة الشرق الأوسط وشمال أفريقيا،' : 'MENA Expertise,'}
            </div>
            <div className="bg-gradient-to-r from-[#1A5F7A] to-[#D4AF37] bg-clip-text text-transparent">
              {i18n.language === 'ar' ? 'وصول عالمي' : 'Global Reach'}
            </div>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {i18n.language === 'ar'
              ? 'نربط الأسواق المحلية بالفرص العالمية من خلال شبكتنا الواسعة وخبرتنا العميقة في المنطقة'
              : 'Connecting local markets with global opportunities through our extensive network and deep regional expertise'}
          </p>
        </div>

        {/* Values Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {values.map((value, index) => (
            <div 
              key={index}
              className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border border-gray-100 hover:border-[#D4AF37]/30 hover:shadow-xl transition-all duration-300 group"
            >
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-[#1A5F7A] to-[#D4AF37] rounded-2xl mb-6 group-hover:scale-110 transition-transform">
                <i className={`${value.icon} text-3xl text-white`}></i>
              </div>
              
              <h3 className="text-2xl font-bold text-[#0A2647] mb-4">
                {i18n.language === 'ar' ? value.titleAr : value.titleEn}
              </h3>
              
              <p className="text-gray-600 leading-relaxed">
                {i18n.language === 'ar' ? value.descAr : value.descEn}
              </p>
            </div>
          ))}
        </div>

        {/* Regional Coverage */}
        <div className="bg-gradient-to-r from-[#0A2647] to-[#1A5F7A] rounded-3xl p-12 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://public.readdy.ai/ai/img_res/02adf3a5315de73b320c210f55c9ba8a.jpg"
              alt="Global Network"
              className="w-full h-full object-cover"
            />
          </div>
          
          <div className="relative z-10 text-center">
            <h3 className="text-3xl font-bold mb-6 leading-tight">
              {i18n.language === 'ar' ? 'نعمل في جميع أنحاء العالم' : 'Operating Across the Globe'}
            </h3>
            <p className="text-lg opacity-90 mb-8 max-w-3xl mx-auto">
              {i18n.language === 'ar'
                ? 'من مقرنا في الدوحة، نسهل التجارة عبر منطقة الشرق الأوسط وشمال أفريقيا وآسيا وأوروبا والأمريكتين، مع التركيز على الأسواق الناشئة والاقتصادات المتقدمة'
                : 'From our Doha headquarters, we facilitate trade across MENA, Asia, Europe, and the Americas, with a focus on emerging markets and established economies'}
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-white/10 rounded-full mx-auto mb-3">
                  <i className="ri-building-line text-2xl text-[#D4AF37]"></i>
                </div>
                <p className="font-medium">{i18n.language === 'ar' ? 'منطقة الشرق الأوسط وشمال أفريقيا' : 'MENA Region'}</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-white/10 rounded-full mx-auto mb-3">
                  <i className="ri-earth-line text-2xl text-[#D4AF37]"></i>
                </div>
                <p className="font-medium">{i18n.language === 'ar' ? 'آسيا والمحيط الهادئ' : 'Asia Pacific'}</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-white/10 rounded-full mx-auto mb-3">
                  <i className="ri-map-pin-line text-2xl text-[#D4AF37]"></i>
                </div>
                <p className="font-medium">{i18n.language === 'ar' ? 'أوروبا' : 'Europe'}</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-white/10 rounded-full mx-auto mb-3">
                  <i className="ri-flight-takeoff-line text-2xl text-[#D4AF37]"></i>
                </div>
                <p className="font-medium">{i18n.language === 'ar' ? 'الأمريكتان' : 'Americas'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
